import { useEffect, useState } from 'react';
import { X, Smartphone, Download } from 'lucide-react';

const STORAGE_KEY = 'jd-pwa-prompt-dismissed';

/**
 * Shows a small "Install Planner App" banner on mobile when the browser
 * fires `beforeinstallprompt` (Android/Chrome) or detects iOS Safari without
 * standalone mode. Lets operators install with one tap on the boat.
 */
const PwaInstallBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Inject the PWA manifest link into <head> for as long as ANY planner
    // page is mounted. It's intentionally NOT in public/index.html because
    // Chrome would then advertise the whole storefront as installable and
    // spam users with "Install app" prompts. Adding it here restricts
    // installability to /planner/* routes (where this banner is used) and
    // removes it again on unmount.
    const MANIFEST_ID = 'jd-pwa-manifest-link';
    if (!document.getElementById(MANIFEST_ID)) {
      const link = document.createElement('link');
      link.id = MANIFEST_ID;
      link.rel = 'manifest';
      link.href = '/manifest.json';
      document.head.appendChild(link);
    }

    // Already dismissed in this browser?
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Already running as a standalone PWA?
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (standalone) return;

    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua) && !/crios|fxios|edgios/.test(ua);
    if (ios) {
      setIsIos(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem(STORAGE_KEY, '1');
      setVisible(false);
    }
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-40 bg-white shadow-xl border-2 border-[#FF6B35] rounded-2xl p-4 flex items-start gap-3" data-testid="pwa-install-banner">
      <div className="bg-[#FF6B35]/10 p-2 rounded-xl shrink-0">
        <Smartphone className="w-6 h-6 text-[#FF6B35]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#0A2342] text-sm">Install Dive Planner</div>
        {isIos ? (
          <p className="text-xs text-gray-600 mt-1">
            Tap <strong>Share</strong> in Safari, then <strong>"Add to Home Screen"</strong>. The app then opens full-screen with no browser bars.
          </p>
        ) : (
          <p className="text-xs text-gray-600 mt-1">
            Install to your home screen for one-tap access on the boat.
          </p>
        )}
        {!isIos && deferred && (
          <button
            onClick={install}
            className="mt-2 inline-flex items-center gap-1 bg-[#0A2342] text-white text-xs px-3 py-1.5 rounded-lg font-medium hover:bg-[#0A2342]/90"
            data-testid="pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5" /> Install now
          </button>
        )}
      </div>
      <button onClick={dismiss} aria-label="Dismiss" className="text-gray-400 hover:text-gray-600 p-1" data-testid="pwa-install-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PwaInstallBanner;
